/**
 * F09 v2 — `/verifications` Requests tab (admin queue v2).
 *
 * Filters (synced to the URL so links are shareable): entity-type chips,
 * multi-status chips (default `pending`), kind, strength, has-duplicate toggle;
 * sort FIFO | score; page-numbered. Columns: entity, kind, strength
 * (`strengthOf(score, riskFlags)`), signals summary, age badge, status.
 * Statuses are sent as REPEATED query params (never `status[]=`).
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  Button,
  Chip,
  EmptyState,
  FormField,
  SegmentedControl,
  Select,
  Spinner,
  Switch,
  Table,
  type TableColumn,
} from '@bravemove/ui-web';
import {
  VERIFIABLE_ENTITY_TYPES,
  VERIFICATION_QUEUE_SORTS,
  VERIFICATION_REQUEST_KINDS,
  VERIFICATION_REQUEST_STATUSES,
  VERIFICATION_STRENGTHS,
  type AdminVerificationQueueItem,
  type VerifiableEntityType,
  type VerificationQueueSort,
  type VerificationRequestKind,
  type VerificationRequestStatus,
  type VerificationStrength,
} from '@bravemove/types';
import {
  adminVerificationApi,
  type VerificationListPage,
  type VerificationQueueQuery,
} from '../../api/verification';
import { AdminPagination } from '../../components/AdminPagination';
import {
  entityLabel,
  kindLabel,
  requestPath,
  requestStatusLabel,
  signalSummary,
  strengthLabel,
} from '../../components/verification/labels';
import {
  AgeBadge,
  EntityTypeBadge,
  RequestStatusBadge,
  StrengthBadge,
  VerificationErrorBanner,
} from '../../components/verification/VerificationBits';
import { parseCsvParam } from '../../lib/verificationQuery';

const PER_PAGE = 25;
const DEFAULT_STATUSES: readonly VerificationRequestStatus[] = ['pending'];
const ALL = 'all';

function pick<T extends string>(raw: string | null, allowed: readonly T[]): T | undefined {
  return raw !== null && (allowed as readonly string[]).includes(raw) ? (raw as T) : undefined;
}

interface LoadState {
  readonly key: string;
  readonly page: VerificationListPage<AdminVerificationQueueItem> | null;
  readonly error: unknown;
}

export function RequestsTab() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const statusParam = searchParams.get('status');
  const statuses: readonly VerificationRequestStatus[] =
    statusParam === null
      ? DEFAULT_STATUSES
      : statusParam === ALL
        ? []
        : parseCsvParam(statusParam, VERIFICATION_REQUEST_STATUSES);
  const entityType = pick<VerifiableEntityType>(searchParams.get('type'), VERIFIABLE_ENTITY_TYPES);
  const requestKind = pick<VerificationRequestKind>(
    searchParams.get('kind'),
    VERIFICATION_REQUEST_KINDS,
  );
  const strength = pick<VerificationStrength>(searchParams.get('strength'), VERIFICATION_STRENGTHS);
  const hasDuplicateFlag = searchParams.get('dup') === 'true';
  const sort: VerificationQueueSort =
    pick<VerificationQueueSort>(searchParams.get('sort'), VERIFICATION_QUEUE_SORTS) ?? 'fifo';
  const page = Math.max(Number.parseInt(searchParams.get('page') ?? '1', 10) || 1, 1);

  const query: VerificationQueueQuery = {
    ...(statuses.length > 0 ? { status: statuses } : {}),
    ...(entityType ? { entityType } : {}),
    ...(requestKind ? { requestKind } : {}),
    ...(strength ? { strength } : {}),
    ...(hasDuplicateFlag ? { hasDuplicateFlag: true } : {}),
    sort,
    page,
    perPage: PER_PAGE,
  };
  const queryKey = JSON.stringify(query);

  const [reloadToken, setReloadToken] = useState(0);
  const [state, setState] = useState<LoadState>({ key: '', page: null, error: null });
  const stateKey = `${queryKey}#${reloadToken}`;

  useEffect(() => {
    let cancelled = false;
    const q = JSON.parse(queryKey) as VerificationQueueQuery;
    const key = `${queryKey}#${reloadToken}`;
    adminVerificationApi.list(q).then(
      (result) => {
        if (!cancelled) setState({ key, page: result, error: null });
      },
      (error: unknown) => {
        if (!cancelled) setState({ key, page: null, error });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [queryKey, reloadToken]);

  const loading = state.key !== stateKey;
  const rows = loading ? [] : (state.page?.items ?? []);
  const pagination = loading ? null : (state.page?.pagination ?? null);

  const setParam = (name: string, value: string | null) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === null || value === '') next.delete(name);
      else next.set(name, value);
      next.delete('page');
      return next;
    });
  };

  const toggleStatus = (s: VerificationRequestStatus) => {
    const nextSet = statuses.includes(s) ? statuses.filter((x) => x !== s) : [...statuses, s];
    setParam('status', nextSet.length === 0 ? ALL : nextSet.join(','));
  };

  const goToPage = (p: number) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (p <= 1) next.delete('page');
      else next.set('page', String(p));
      return next;
    });

  const columns: TableColumn<AdminVerificationQueueItem>[] = [
    {
      key: 'entity',
      header: t('admin:verification.queue.columns.entity'),
      cell: (r) => (
        <div className="min-w-0 space-y-0.5">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-semibold text-ink">{r.entityName}</span>
            <EntityTypeBadge type={r.entityType} />
            {r.hasDuplicateFlag ? (
              <Badge tone="danger" size="sm" data-testid={`dup-flag-${r.id}`}>
                {t('admin:verification.queue.duplicateFlag')}
              </Badge>
            ) : null}
          </div>
          {r.subjectDisplayName && r.subjectDisplayName !== r.entityName ? (
            <div className="text-[11px] text-ink-3">{r.subjectDisplayName}</div>
          ) : null}
        </div>
      ),
    },
    {
      key: 'kind',
      header: t('admin:verification.queue.columns.kind'),
      cell: (r) => kindLabel(t, r.requestKind),
    },
    {
      key: 'strength',
      header: t('admin:verification.queue.columns.strength'),
      cell: (r) => <StrengthBadge score={r.evaluatorScore} riskFlags={r.riskFlags} />,
    },
    {
      key: 'signals',
      header: t('admin:verification.queue.columns.signals'),
      cell: (r) => (
        <span className="text-[12px] text-ink-2">{signalSummary(t, r.signalHighlights)}</span>
      ),
    },
    {
      key: 'age',
      header: t('admin:verification.queue.columns.age'),
      cell: (r) => <AgeBadge since={r.queuedAt ?? r.submittedAt ?? r.createdAt} />,
    },
    {
      key: 'status',
      header: t('admin:verification.queue.columns.status'),
      cell: (r) => <RequestStatusBadge status={r.status} />,
    },
  ];

  return (
    <div className="space-y-4" data-testid="verification-requests-tab">
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2" data-testid="queue-entity-chips">
          <span className="text-[12px] font-semibold text-ink-2">
            {t('admin:verification.queue.entityFilterLabel')}
          </span>
          <Chip
            selected={!entityType}
            role="button"
            tabIndex={0}
            onClick={() => setParam('type', null)}
            data-testid="queue-type-all"
          >
            {t('admin:verification.queue.allTypes')}
          </Chip>
          {VERIFIABLE_ENTITY_TYPES.map((type) => (
            <Chip
              key={type}
              selected={entityType === type}
              role="button"
              tabIndex={0}
              onClick={() => setParam('type', entityType === type ? null : type)}
              data-testid={`queue-type-${type}`}
            >
              {entityLabel(t, type)}
            </Chip>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="queue-status-chips">
          <span className="text-[12px] font-semibold text-ink-2">
            {t('admin:verification.queue.statusLabel')}
          </span>
          {VERIFICATION_REQUEST_STATUSES.map((s) => (
            <Chip
              key={s}
              selected={statuses.includes(s)}
              role="button"
              tabIndex={0}
              aria-pressed={statuses.includes(s)}
              onClick={() => toggleStatus(s)}
              data-testid={`queue-status-${s}`}
            >
              {requestStatusLabel(t, s)}
            </Chip>
          ))}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <FormField label={t('admin:verification.queue.kindLabel')} className="w-44">
            <Select
              size="sm"
              value={requestKind ?? ''}
              options={[
                { value: '', label: t('admin:verification.queue.anyKind') },
                ...VERIFICATION_REQUEST_KINDS.map((k) => ({ value: k, label: kindLabel(t, k) })),
              ]}
              data-testid="queue-kind"
              onChange={(e) => setParam('kind', e.target.value || null)}
            />
          </FormField>
          <FormField label={t('admin:verification.queue.strengthLabel')} className="w-44">
            <Select
              size="sm"
              value={strength ?? ''}
              options={[
                { value: '', label: t('admin:verification.queue.anyStrength') },
                ...VERIFICATION_STRENGTHS.map((s) => ({ value: s, label: strengthLabel(t, s) })),
              ]}
              data-testid="queue-strength"
              onChange={(e) => setParam('strength', e.target.value || null)}
            />
          </FormField>
          <div className="pb-2">
            <Switch
              checked={hasDuplicateFlag}
              label={t('admin:verification.queue.duplicateToggle')}
              data-testid="queue-dup-toggle"
              onChange={(e) => setParam('dup', e.target.checked ? 'true' : null)}
            />
          </div>
          <div className="ms-auto flex items-center gap-2 pb-1">
            <span className="text-[12px] font-semibold text-ink-2">
              {t('admin:verification.queue.sortLabel')}
            </span>
            <SegmentedControl<VerificationQueueSort>
              size="sm"
              value={sort}
              aria-label={t('admin:verification.queue.sortLabel')}
              options={[
                { value: 'fifo', label: t('admin:verification.queue.sortFifo') },
                { value: 'score', label: t('admin:verification.queue.sortScore') },
              ]}
              onChange={(v) => setParam('sort', v === 'fifo' ? null : v)}
            />
          </div>
        </div>
      </div>

      {!loading && state.error ? (
        <div className="space-y-2">
          <VerificationErrorBanner error={state.error} testId="queue-error" />
          <Button size="sm" variant="secondary" onClick={() => setReloadToken((n) => n + 1)}>
            {t('admin:verification.common.retry')}
          </Button>
        </div>
      ) : null}

      {loading ? (
        <div className="flex justify-center py-12" data-testid="queue-loading">
          <Spinner size={28} />
        </div>
      ) : !state.error ? (
        <>
          <Table
            data={rows}
            columns={columns}
            rowKey={(r) => r.id}
            ariaLabel={t('admin:verification.queue.tableAria')}
            onRowPress={(r) => navigate(requestPath(r.id))}
            empty={<EmptyState title={t('admin:verification.queue.empty')} />}
          />
          {pagination ? (
            <AdminPagination
              pagination={pagination}
              onChange={goToPage}
              shownCount={rows.length}
              testId="verification-queue-pagination"
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}
